import React, { useEffect, useState } from "react";
import {
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  GSButton,
  GSItemList,
  useCollapsable,
} from "golfstatus_react_components";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";

// import scss
import "./tournament-round-change-round.scss";
import {
  getRoundGroups,
  getRoundName,
  getSubRounds,
} from "../../../hooks/round/roundHooks";

//the rounds a hole assignments page can switch to, sliding open under its header.
//a linked round opens on its own sub rounds, anything else on the tournament's
//round numbers, where a number holding sub rounds opens into them.
const TournamentRoundChangeRound = (props) => {
  const { currentRound, rounds, open, onClose } = props;

  const navigate = useNavigate();
  const match = useMatch("/:tournamentID/rounds/:roundID/hole-assignments");

  const [changingRoundNumber, setChangingRoundNumber] = useState();

  //the section slides between zero and the height of whatever it holds, and
  //remeasures itself as the options open into a number's sub rounds.
  const [, , , , , setRoundOptionsHidden, , animateRoundOptions] =
    useCollapsable(!open);

  //rounds that share this round's number are the sub rounds it can be swapped
  //for. without them the section moves between the tournament's rounds instead,
  //and with only one round there is nothing to change to at all.
  const linkedRounds = getSubRounds(currentRound, rounds);
  const hasSubRounds = linkedRounds?.length > 1;

  //one entry per round number, so a number holding sub rounds is a single option
  //that opens into them.
  const roundGroups = getRoundGroups(
    [...(rounds ?? [])].sort((a, b) => a?.number - b?.number)
  );
  const changingGroup = roundGroups?.find?.(
    (g) => g?.number === changingRoundNumber
  );
  const hasOtherRounds = roundGroups?.length > 1;
  const canChangeRound = hasSubRounds || hasOtherRounds;

  useEffect(() => {
    setRoundOptionsHidden(!open);
  }, [open, setRoundOptionsHidden]);

  //a linked round opens on its own sub rounds, an unlinked one on the list of
  //rounds it can move to.
  useEffect(() => {
    if (open) {
      setChangingRoundNumber(hasSubRounds ? currentRound?.number : undefined);
    }
  }, [open, hasSubRounds, currentRound?.number]);

  const roundSelected = (round) => {
    if (round?.id !== currentRound?.id) {
      setChangingRoundNumber(undefined);
      onClose?.();
      navigate(
        `/${match?.params?.tournamentID}/rounds/${round?.id}/hole-assignments`,
        { replace: true }
      );
    }
  };

  //a number holding sub rounds opens into them, anything else is a round to go
  //straight to.
  const roundNumberSelected = (group) => {
    if (group?.rounds?.length > 1) {
      setChangingRoundNumber(group?.number);
      return;
    }
    roundSelected(group?.rounds?.[0]);
  };

  const getRoundButton = (round) => ({
    id: round?.id,
    type: round?.id === currentRound?.id ? "black" : "light-grey",
    title: `Round ${getRoundName(round, rounds)}`,
    onClick: () => roundSelected(round),
  });

  const getRoundOptionButtons = () => {
    //with nothing but this round's sub rounds, there is no list to start from.
    if (hasSubRounds && !hasOtherRounds) {
      return linkedRounds?.map?.((round) => getRoundButton(round));
    }

    //a number that was opened shows the way back to the round list first.
    if (changingGroup) {
      return [
        {
          id: "back",
          type: "light-grey",
          buttonIcon: faChevronLeft,
          onClick: () => setChangingRoundNumber(undefined),
        },
        ...(changingGroup?.rounds?.map?.((round) => getRoundButton(round)) ??
          []),
      ];
    }

    return roundGroups?.map?.((group) => ({
      id: group?.number,
      type: group?.number === currentRound?.number ? "black" : "light-grey",
      title: `Round ${group?.number}`,
      onClick: () => roundNumberSelected(group),
    }));
  };

  const getRoundOptionsLabel = () => {
    if (hasSubRounds && !hasOtherRounds) {
      return `Round ${currentRound?.number}`;
    }
    if (changingGroup) {
      return `Rounds / Round ${changingGroup?.number}`;
    }
    return "Rounds";
  };

  return (
    <tournament-round-change-round>
      {/* the collapsable always wraps an element so it has something to
          measure, even on a round with nothing to change to. the divider is an
          inset shadow rather than a border, which the measured height leaves
          out. */}
      {animateRoundOptions(
        <div
          className={"border-bottom"}
          style={{
            ...defaultLayouts.vStack,
            ...defaultLayouts.fullWidth,
            ...defaultPadding.xLargePad,
            ...defaultGapping.mediumGap,
          }}
        >
          {canChangeRound && (
            <>
              <div
                //tertiary sits at the same size as the row above it, so this
                //steps down to the x-small label instead.
                style={{
                  ...defaultTypography.xSmallBodyBold,
                  opacity: 0.5,
                  ...defaultTypography.uppercase,
                }}
              >
                {getRoundOptionsLabel()}
              </div>
              <GSItemList
                type="horizontal medium-large-gap"
                items={getRoundOptionButtons()}
                listItem={(button) => <GSButton {...button} />}
              />
            </>
          )}
        </div>
      )}
    </tournament-round-change-round>
  );
};

export default TournamentRoundChangeRound;
